import React, { useContext, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { AxiosResponse } from 'axios';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import OrdersApi from '../../../../services/api/OrdersApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL, SHIPPING_TYPES, FILE_UPLOAD_TYPES, ROLES } from '../../../../utils/constants';
import AutocompleteStaticComponent from '../../../../components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { constantsToAutocomplete } from '../../../../utils/misc';
import SecondaryNavigation from '../../../../components/common/navigation/SecondaryNavigation';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import DatePickerInputComponent from '~/src/components/common/forms/DatePickerInput/DatePickerInputComponent';
import TertiaryButton from '~/src/components/common/forms/Button/TertiaryButton';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import RolesDependent from '~/src/components/common/RolesDependent';
import MyContext from '~/src/services/context';
import CustomersApi from '~/src/services/api/CustomersApi';
import AutocomplitePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';
import { OrderGetMinDto } from '~/src/types/OrderGetMinDto';
import { orderGetMinDtoToEmptyFormik } from '~/src/types/convertors/orderGetMinDtoToEmptyFormik';
import { orderGetMinDtoToFormik } from '~/src/types/convertors/orderGetMinDtoToFormik';

interface ParamProps {
  id?: string;
}

export default function OrderEdit(): React.ReactElement {
  const { id } = useParams<ParamProps>();
  const context = useContext(MyContext);
  const [itemData, setItemData] = useState<OrderGetMinDto | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [customerUrl, setCustomerUrl] = useState(`${API_URL}/contractors/customers/get`);
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    const containerId = values.containerId || null;
    const convertedValues = {
      ...values,
      containerId,
      orderType: itemData?.orderType,
    };
    let result: AxiosResponse<OrderGetMinDto> | null = null;

    if (id) {
      result = await OrdersApi.update(id, convertedValues);
    } else {
      result = await OrdersApi.create(convertedValues);
    }

    if (result.status === StatusCodes.OK) {
      const orderId = result.data?.id;
      history.push(`/orders/details/${orderId}`);
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async (orderId: string): Promise<void> => {
    const result = await OrdersApi.get(orderId);
    setItemData(result.data);
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, [id]);

  const handleBack = () => {
    history.push('/orders');
  };

  const getInitialValues = (() => {
    if (!id) {
      return orderGetMinDtoToEmptyFormik();
    } else {
      if (itemData) {
        return orderGetMinDtoToFormik(itemData);
      } else {
        return null;
      }
    }
  })();

  const fetchAvailableCustomers = async (warehouseOwnerId: string, setFieldValue: (field: string, value: any) => void) => {
    if (!warehouseOwnerId) {
      setAvailableCustomers([]);
      setCustomerUrl(`${API_URL}/contractors/customers/get`);
      setFieldValue('customerId', '');
    } else {
      try {
        const response = await CustomersApi.getAvailableCustomers(warehouseOwnerId);

        if (response.data.data.length > 0) {
          setAvailableCustomers(response.data.data);
        } else {
          setAvailableCustomers([]);
          setCustomerUrl(`${API_URL}/contractors/customers/get`);
        }
      } catch (error) {
        setCustomerUrl(`${API_URL}/contractors/customers/get`);
      }
    }
  };

  return (
    <>
      <WhitePad>
        {id && <SecondaryNavigation />}

        <TitleComponent
          onLeftHandler={(): void => {
            history.push('/orders-list');
          }}
          title={`${id ? 'Edit ' : 'Create '} Order ${id ? '#' + itemData?.orderNumber : ''}`}
        />

        {getInitialValues && (
          <Formik
            initialValues={getInitialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              saveItem(values, setSubmitting);
            }}
          >
            {({
              initialValues,
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              errors,
              touched,
            }) => {
              useEffect(() => {
                fetchAvailableCustomers(values.warehouseOwnerId, setFieldValue);
              }, [values.warehouseOwnerId]);

              return (
                <FormWrapper width="100%">
                  <form onSubmit={handleSubmit} autoComplete="off">
                    <FormRow>
                      <RolesDependent
                        roles={[
                          ROLES.ADMIN,
                          ROLES.SUPER_ADMIN,
                          ROLES.WAREHOUSE_ADMIN,
                          ROLES.WAREHOUSE,
                          ROLES.OFFICE,
                          ROLES.SALES,
                          ROLES.DATABASE_ADMIN,
                          ROLES.SVILA_CLIENT_ADMIN
                        ]}
                      >
                        <AutocompleteComponent
                          url={`${API_URL}/contractors/warehouse-owners/get`}
                          name="warehouseOwnerId"
                          label="warehouseOwnerId"
                          initialValue={values.warehouseOwnerId}
                          onChange={setFieldValue}
                          getOptionLabel={(option) => `${option.name}`}
                          isMulti={false}
                        />
                      </RolesDependent>
                      <AutocomplitePaginatedComponent
                        key={values.customerId}
                        url={(availableCustomers.length === 0 || values.warehouseOwnerId === '')
                          ? customerUrl
                          : `${API_URL}/contractors/customers/get-available-customers/${values.warehouseOwnerId}`}
                        name="customerId"
                        label="customerId"
                        initialValue={values.customerId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        errorMessage={errors.customerId}
                        style={{ borderRadius: 200 }}
                      />
                    </FormRow>
                    <FormRow>
                      <AutocompleteComponent
                        url={`${API_URL}/products/pallets/get`}
                        name="palletId"
                        label="palletId"
                        initialValue={values.palletId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        cols={3}
                        errorMessage={errors.palletId}
                      />
                    </FormRow>
                    <FormRow>
                      <AutocompleteComponent
                        url={`${API_URL}/products/containers/get`}
                        name="containerId"
                        label="containerId"
                        initialValue={values.containerId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        disabled={values.shippingType === 'Air'}
                        errorMessage={errors.containerId}
                      />
                      <AutocompleteStaticComponent
                        options={constantsToAutocomplete(SHIPPING_TYPES)}
                        name="shippingType"
                        initialValue={values.shippingType}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.label}`}
                        cols={3}
                        errors={errors}
                      />
                    </FormRow>

                    <FormRow>
                      <TextInputComponent
                        name="palletMaxWidth"
                        label="Pallet Max Width (cm)"
                        value={values.palletMaxWidth}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="number"
                        cols={3}
                        disabled={values.shippingType === 'Sea'}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="palletMaxHeight"
                        label="Pallet Max Height (cm)"
                        value={values.palletMaxHeight}
                        onBlur={handleBlur}
                        cols={3}
                        onChange={handleChange}
                        type="number"
                        disabled={values.shippingType === 'Sea'}
                      />
                      <TextInputComponent
                        name="palletMaxLength"
                        label="Pallet Max Length (cm)"
                        value={values.palletMaxLength}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        disabled={values.shippingType === 'Sea'}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="maxKg"
                        label="Pallet Max Weight (kg)"
                        value={values.maxKg}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <DatePickerInputComponent
                        label="dateOfTransaction"
                        name="dateOfTransaction"
                        onChange={(e): void => {
                          setFieldValue('dateOfTransaction', e.value);
                        }}
                        defaultValue={initialValues.dateOfTransaction}
                        touched={touched}
                        errors={errors}
                        cols={3}
                        value={values.dateOfTransaction}
                      />
                      <DatePickerInputComponent
                        label="dateOfTransport"
                        name="dateOfTransport"
                        onChange={(e): void => {
                          setFieldValue('dateOfTransport', e.value);
                        }}
                        defaultValue={initialValues.dateOfTransport}
                        touched={touched}
                        errors={errors}
                        cols={3}
                        value={values.dateOfTransport}
                      />
                      <TextInputComponent
                        name="destination"
                        label="Destination Place"
                        value={values.destination}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                      />
                      <TextInputComponent
                        name="containerLine"
                        label="Container Line"
                        value={values.containerLine}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="containerNo"
                        label="ContainerNo"
                        value={values.containerNo}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="vesselFlag"
                        label="Vessel/Flag"
                        value={values.vesselFlag}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="truckTrailer"
                        label="Truck/Trailer"
                        value={values.truckTrailer}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="routing"
                        label="Routing"
                        value={values.routing}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="palletsDescription"
                        label="Pallets Description"
                        value={values.palletsDescription}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="totalWeightOfOrder"
                        label="Total Weight Of Order"
                        value={values.totalWeightOfOrder}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="salesTerm"
                        label="Sales Term"
                        value={values.salesTerm}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="paymentTerm"
                        label="Payment Term"
                        value={values.paymentTerm}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="cosigneeName"
                        label="Cosignee Name"
                        value={values.cosigneeName}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="cosigneeAddress"
                        label="Cosignee Address"
                        value={values.cosigneeAddress}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                    </FormRow>
                    <div className="d-flex justify-content-between">
                      <TertiaryButton onClick={handleBack} title="Back" />
                      <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Next" />
                    </div>
                  </form>
                </FormWrapper>
              );
            }}
          </Formik>
        )}
      </WhitePad>
    </>
  );
}